import React, { useMemo } from 'react'
import { graphql, HeadProps, Link, PageProps } from 'gatsby'
import { AppHead, Layout } from '../components/common'
import { Hero } from '../components/page/home/Hero'
import { FirmCarousel } from '../components/page/home/FirmCarousel/FirmCarousel'
import { LamatchSection } from '../components/page/home/LamatchSection'
import { FimsSection } from '../components/page/home/FirmsSection'
import { LamadashSection } from '../components/page/home/LamadashSection'
import { SectionContainer } from '../components/page/SectionContainer'
import { LinkButton } from '../components/common/menu/linkButton'
import { TestimonialsSection } from '../components/page/home/Testimonial'
import { HomeHeading } from '../components/page/home/Heading'
import { TitleHighlight } from '../components/ui/typo/TitleHighlight'
import { FirmInfosResult, FirmTeaserMapper } from '../components/home/mapper'

const Home: React.FC<PageProps<Queries.HomeQuery>> = ({ data, pageContext, location }) => {
  const { firms = [] } = pageContext as { firms: FirmInfosResult[] }
  const firmsToDisplay = useMemo(() => firms.map(FirmTeaserMapper) || [], [firms])

  return (
    <Layout
      careerSite={false}
      apiToken={data.site?.siteMetadata?.frontStrapiToken || undefined}
      apiUrl={data.site?.siteMetadata?.siteStrapiUrl || undefined}
      location={location}
    >
      <div className="py-16 space-y-8 md:py-24 bg-very-light-blue">
        <Hero />
        <FirmCarousel firms={firmsToDisplay} />
        <LamatchSection />
        <FimsSection />

        <section className="py-20 text-white bg-night">
          <div className="container max-w-3xl px-4 mx-auto space-y-6 xl:space-y-8">
            <div className="flex items-center justify-center">
              <img
                src="/trophee.webp"
                alt="Trophé"
                className="w-24 h-24 md:w-40 md:h-40 lg:w-48 lg:h-48"
              />
            </div>
            <HomeHeading additionalClasses="text-white text-center">
              Votre équipe mérite ses talents de demain
            </HomeHeading>

            <p className="px-2 text-lg text-center">
              Votre équipe mérite les talents qui lui permetteront de se
              développer et d&apos;assurer des missions de qualité auprès des
              clients. Montrez à ces talents pourquoi ils doivent vous
              rejoindre.
            </p>
            <div className="flex justify-center">
              <Link
                className="px-4 py-2 text-center bg-white rounded-full text-night md:py-3 md:px-5"
                to="/je-suis-un-cabinet"
              >
                Créer ma page employeur
              </Link>
            </div>
          </div>
        </section>

        <LamadashSection />

        <SectionContainer>
          <HomeHeading additionalClasses="text-center md:text-left">
            Pourquoi <TitleHighlight>Lamacompta</TitleHighlight>&nbsp;?
          </HomeHeading>
          <div className="space-y-6 text-lg text-center text-night md:text-left">
            <p>
              Chez Lamacompta, nous voulons participer à la construction d’un{' '}
              <strong>futur du travail durable</strong>. Un futur, où le travail
              n’est pas une fin, mais un moyen de s‘accomplir.
            </p>
            <p>
              Depuis notre création en 2020, notre approche est{' '}
              <strong>
                centrée sur les critères de recherche des candidats
              </strong>
              , afin de maximiser les mises en relation réussies. Pourquoi le
              lama ? Ça, il faudra nous le demander...
            </p>

            <div className="flex items-center justify-center md:block">
              <LinkButton
                label="Qui sommes nous ?"
                href="/cabinets/lamacompta"
                isInverted
              />
            </div>
          </div>
        </SectionContainer>

        <TestimonialsSection />
      </div>
    </Layout>
  )
}

export default Home

export const query = graphql`
  query Home {
    site {
      siteMetadata {
        siteStrapiUrl
        siteUrl
        frontStrapiToken
      }
    }
  }
`

export const Head: React.FC<HeadProps> = () => (
  <AppHead
    title="N°1 de l'Emploi en Expertise Comptable- Lamacompta"
    metaTitle="Lamacompta: N°1 de l'Emploi en Expertise Comptable"
    metaDescription="Trouvez votre emploi en comptabilité. Collaborateur comptable, Juriste, Gestionnaire de paie, auditeur etc. en cabinet d'expertise comptable."
    metaImage="https://lamacompta.co/lamacompta-sharing-default.jpg"
    linkCanonical="https://lamacompta.co"
  />
)
